import dayjs from "dayjs";

import { EVENT_LABELS, RECOVERY_STATUS, USAGE_STATUS } from "./constants";

const numberFormatter = new Intl.NumberFormat("pt-BR");
const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

export function formatNumber(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return numberFormatter.format(Number(value));
}

export function formatDecimal(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

export function formatCurrency(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return currencyFormatter.format(Number(value));
}

export function safeDivide(numerator, denominator) {
  if (numerator === null || numerator === undefined) return null;
  if (denominator === null || denominator === undefined) return null;
  if (Number(denominator) === 0) return 0;
  return Number(numerator) / Number(denominator);
}

export function formatPercent(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return `${(Number(value) * 100).toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
}

export function formatDate(value) {
  if (!value) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-");
    return `${day}/${month}/${year}`;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
}

export function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    // hour: "2-digit",
    // minute: "2-digit"

  });
}

export function getLatestDaily(daily = []) {
  return daily.length ? daily[daily.length - 1] : null;
}

export function getMostRecentResponsible(attempt) {
  const contacts = Array.isArray(attempt?.contacts) ? attempt.contacts : [];
  return contacts.length ? contacts[contacts.length - 1]?.responsavelNome || "—" : "—";
}

export function getResultyStatus(status) {
  return RECOVERY_STATUS[status] || {
    label: status || "Sem status",
    className: "bg-gray-100 text-gray-700",
  };
}

export function getUsageStatus(status) {
  return USAGE_STATUS[status] || {
    label: status || "Não informado",
    className: "bg-gray-100 text-gray-700",
  };
}

export function getEventLabel(eventName) {
  return EVENT_LABELS[eventName] || eventName || "Evento não identificado";
}

export function aggregateEventBreakdown(daily = []) {
  const hideEvent = ["customer_pets_viewed", "config_viewed", "recovery_kpis_viewed", "recovery_attempts_viewed"]

  return daily.reduce((result, item) => {
    Object.entries(item.eventBreakdown || {}).forEach(([eventName, total]) => {
      if (hideEvent.includes(eventName)) return;

      result[eventName] = (result[eventName] || 0) + Number(total || 0);
    });
    return result;
  }, {});
}

function toMetricNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function getWeekStart(value) {
  const date = dayjs(value);
  const weekday = date.day();
  const diffToMonday = weekday === 0 ? 6 : weekday - 1;
  return date.subtract(diffToMonday, "day").startOf("day");
}

function getWeekEnd(value) {
  return getWeekStart(value).add(6, "day");
}

function createUsageBucket(item, mode) {
  const storeDate = dayjs(item.storeDate);

  if (mode === "monthly") {
    return {
      key: storeDate.startOf("month").format("YYYY-MM"),
      label: storeDate.format("MM/YY"),
      sortValue: storeDate.startOf("month").valueOf(),
      periodLabel: storeDate.format("MM/YYYY"),
    };
  }

  if (mode === "weekly") {
    const weekStart = getWeekStart(storeDate);
    const weekEnd = getWeekEnd(storeDate);

    return {
      key: weekStart.format("YYYY-MM-DD"),
      label: `${weekStart.format("DD/MM")} - ${weekEnd.format("DD/MM")}`,
      sortValue: weekStart.valueOf(),
      periodLabel: `${weekStart.format("DD/MM/YY")} - ${weekEnd.format("DD/MM/YY")}`,
    };
  }

  return {
    key: storeDate.format("YYYY-MM-DD"),
    label: formatDate(storeDate.format("YYYY-MM-DD")).slice(0, 5),
    sortValue: storeDate.valueOf(),
    periodLabel: formatDate(storeDate.format("YYYY-MM-DD")),
  };
}

export function aggregateUsageByViewMode(daily = [], mode = "daily") {
  if (mode === "daily") {
    return daily.map((item) => {
      const bucket = createUsageBucket(item, mode);

      return {
        ...item,
        ...bucket,
      };
    });
  }

  const grouped = daily.reduce((result, item) => {
    const bucket = createUsageBucket(item, mode);
    const current = result[bucket.key] || {
      ...bucket,
      eligibleOpportunities: 0,
      pendingContacts: 0,
      contactsMade: 0,
      views: 0,
    };

    current.eligibleOpportunities += toMetricNumber(item.eligibleOpportunities);
    current.pendingContacts += toMetricNumber(item.pendingContacts);
    current.contactsMade += toMetricNumber(item.contactsMade);
    current.views += toMetricNumber(item.views);

    result[bucket.key] = current;
    return result;
  }, {});

  return Object.values(grouped).sort((first, second) => first.sortValue - second.sortValue);
}
